import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RESTAURANT_SUBTAGS } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";
import type { Bucket, BucketId } from "../lib/buckets";
import type { DiscoverCity, DiscoverItem } from "../types";

/**
 * The filter rows above the mobile Discover sheet's venue grid: categories,
 * cities, kinds, restaurant sub-tags.
 *
 * They live here rather than in category-strip.tsx / subtag-chips.tsx because
 * every one of them used to be a sideways scroller, and the owner ruled on
 * 2026-08-17 that MaJourneys never scrolls a row of options sideways — half
 * the choices end up behind a gesture the traveller may never discover. On a
 * 375px phone the old category strip cut "Spas" in half, which is exactly the
 * failure the rule describes. Everything here wraps, and the long rows
 * collapse behind a "+N more" instead of running off the edge.
 *
 * The phone (apps/mobile explore-surface.tsx) does scroll these rows; it is a
 * native app where a horizontal swipe is a known gesture. This is the one
 * place this surface deliberately does not copy it.
 */

/** How many pills a wrapping row shows before it collapses the rest. */
const COLLAPSED_PILLS = 8;

export interface SubFilterOption {
  /** The venue's own kind label ("Villa", "Riad", "Camp") — already localized. */
  readonly key: string;
  readonly count: number;
}

/**
 * The kinds worth offering under the selected category, counted off the rows
 * actually on screen.
 *
 * Derived rather than configured, so a category only offers the kinds it
 * really holds and a new one appears on its own the day it is seeded.
 *
 * Two rulings from the phone, kept verbatim in behaviour:
 * a pill that holds everything in view narrows nothing ("Restaurants (165)"
 * inside Restaurants is the whole list wearing a filter's clothes), and one
 * kind is not a choice — so a row that cannot narrow is dropped entirely
 * rather than shown greyed or shown alone.
 *
 * Restaurants are excluded because they already carry the editorial sub-tag
 * chips (rooftop, bar, lounge) directly above; two rows doing one job could be
 * set against each other and return nothing at all.
 */
export function deriveSubFilters(
  items: readonly DiscoverItem[],
  bucketId: BucketId | null,
): readonly SubFilterOption[] {
  if (!bucketId || bucketId === "restaurants") return [];
  const counts = new Map<string, number>();
  for (const item of items) {
    const label = item.pinLabel?.trim();
    if (!label) continue;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const all = [...counts.entries()].map(([key, count]) => ({ key, count }));
  const total = all.reduce((n, option) => n + option.count, 0);
  const narrowing = all.filter((option) => option.count < total);
  // Kind labels have no natural order, so the biggest group leads. Ties fall
  // back to the label so the row does not reshuffle between renders.
  narrowing.sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
  return narrowing.length > 1 ? narrowing : [];
}

/**
 * The phone sheet's categories as one sideways row of pills: the site's 3D
 * category art, then the label (owner, web suggestions lab idea 52). It
 * replaced the four-across grid of pastel discs, which took two rows of a
 * small screen from the map.
 *
 * This row is the owner's exception to the wrap-don't-scroll rule above. The
 * scroller clips its own top and bottom, so it carries 4px of padding above
 * and below and cancels it with negative margins (see `.mj-dsheet-catrow`).
 * The pills never cast a drop shadow; chosen shows as the navy face.
 */
export function CategoryChipRow({
  buckets,
  activeId,
  onSelect,
  art,
}: {
  readonly buckets: readonly Bucket[];
  readonly activeId: BucketId | null;
  readonly onSelect: (id: BucketId) => void;
  readonly art?: Partial<Record<BucketId, string>>;
}) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t("discover.filter.type")} className="mj-dsheet-catrow">
      {buckets.map((bucket) => {
        const selected = bucket.id === activeId;
        const src = art?.[bucket.id];
        return (
          <button
            key={bucket.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(bucket.id)}
            className="mj-dsheet-catchip"
            style={{
              background: selected ? BEVEL_NAVY_FACE : BEVEL_WHITE_FACE,
              color: selected ? "#ffffff" : "#110E39",
              boxShadow: selected
                ? BEVEL_CHIP_ON
                : `${BEVEL_INSET_WHITE}, inset 0 0 0 1px rgba(4, 0, 51, 0.12)`,
            }}
          >
            {src ? <img src={src} alt="" className="mj-dsheet-catchip__art" /> : null}
            {t(bucket.labelKey)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A wrapping row of pills that hides its tail behind a "+N more" once it grows
 * past `COLLAPSED_PILLS`.
 *
 * Collapsing rather than scrolling is the sanctioned way out of a long row:
 * every option is one press away and none of them is hidden off the side of
 * the screen. Anything already selected is pulled into the visible head, so
 * the traveller can always see — and undo — the filter they are looking at.
 */
function WrapPillRow({
  label,
  ariaLabel,
  children,
}: {
  readonly label?: string;
  readonly ariaLabel: string;
  readonly children: readonly React.ReactNode[];
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hidden = Math.max(0, children.length - COLLAPSED_PILLS);
  const shown = expanded ? children : children.slice(0, COLLAPSED_PILLS);
  return (
    <div className="mj-dsheet-facet">
      {label && <p className="mj-dsheet-facet__label">{label}</p>}
      <div role="group" aria-label={ariaLabel} className="mj-dsheet-facet__row">
        {shown}
        {hidden > 0 && (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="mj-dsheet-more"
          >
            {expanded
              ? t("discover.show_less")
              : t("discover.show_more_count", { count: hidden })}
          </button>
        )}
      </div>
    </div>
  );
}

function Pill({
  label,
  count,
  active,
  onClick,
}: {
  readonly label: string;
  readonly count?: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={active ? "mj-dsheet-pill mj-dsheet-pill--on" : "mj-dsheet-pill"}
    >
      <span>{label}</span>
      {count !== undefined && (
        <span className="mj-dsheet-pill__count">{count}</span>
      )}
    </button>
  );
}

/**
 * The city row, ported from the phone's `CityPill` header: every city that has
 * something under the live filters, each with the count it would give you, so
 * a city is never a blind pick. Pressing the selected one again clears it.
 */
export function CityPillRow({
  cities,
  counts,
  activeCity,
  onPickCity,
}: {
  readonly cities: readonly DiscoverCity[];
  readonly counts: ReadonlyMap<string, number>;
  readonly activeCity: string | null;
  readonly onPickCity: (slug: string | null) => void;
}) {
  const { t } = useTranslation();
  // The chosen city is pulled to the front so it survives the collapse — a
  // filter you cannot see is a filter you cannot take off.
  const ordered = useMemo(() => {
    if (!activeCity) return cities;
    const picked = cities.filter((c) => c.slug === activeCity);
    return picked.length === 0
      ? cities
      : [...picked, ...cities.filter((c) => c.slug !== activeCity)];
  }, [cities, activeCity]);

  return (
    <WrapPillRow
      label={t("discover.jump_to_city")}
      ariaLabel={t("discover.filter.city")}
    >
      {[
        <Pill
          key="__all"
          label={t("discover.filter.all_cities")}
          active={activeCity === null}
          onClick={() => onPickCity(null)}
        />,
        ...ordered.map((city) => (
          <Pill
            key={city.slug}
            label={city.name}
            count={counts.get(city.slug) ?? 0}
            active={activeCity === city.slug}
            onClick={() => onPickCity(activeCity === city.slug ? null : city.slug)}
          />
        )),
      ]}
    </WrapPillRow>
  );
}

/** The derived kind row (Villa / Riad / Camp). Rendered only when it narrows. */
export function SubFilterRow({
  options,
  active,
  onSelect,
}: {
  readonly options: readonly SubFilterOption[];
  readonly active: string | null;
  readonly onSelect: (key: string | null) => void;
}) {
  const { t } = useTranslation();
  if (options.length === 0) return null;
  return (
    <WrapPillRow ariaLabel={t("discover.filter.kind")}>
      {[
        <Pill
          key="__all"
          label={t("discover.subfilter_all")}
          active={active === null}
          onClick={() => onSelect(null)}
        />,
        ...options.map((option) => (
          <Pill
            key={option.key}
            label={option.key}
            count={option.count}
            active={active === option.key}
            onClick={() => onSelect(active === option.key ? null : option.key)}
          />
        )),
      ]}
    </WrapPillRow>
  );
}

/**
 * Restaurant sub-tags (rooftop, bar, lounge). Multi-select, OR semantics via
 * matchesSubtags. Same wrapping row as everything else here — the shared
 * subtag-chips.tsx component still scrolls sideways, which this surface may
 * not do.
 */
export function SubtagPillRow({
  selected,
  onToggle,
}: {
  readonly selected: readonly string[];
  readonly onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <WrapPillRow ariaLabel={t("discover.filter.kind")}>
      {RESTAURANT_SUBTAGS.map((tag) => (
        <Pill
          key={tag.id}
          label={t(tag.labelKey)}
          active={selected.includes(tag.id)}
          onClick={() => onToggle(tag.id)}
        />
      ))}
    </WrapPillRow>
  );
}
